import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, getImageUrl } from '../config/api';
import DateTimeDisplay from './DateTimeDisplay';
import AdminCardAssignmentModal from './AdminCardAssignmentModal';
import Tooltip from './Tooltip';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [newClass, setNewClass] = useState('');
  const [selectedClass, setSelectedClass] = useState('');

  // Admin role and permissions
  const [currentAdmin, setCurrentAdmin] = useState(() => {
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    return adminUser;
  });
  const [adminUsers, setAdminUsers] = useState([]);
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [requestStats, setRequestStats] = useState({ pending: 0 });
  const [feedbackStats, setFeedbackStats] = useState({ pending: 0 });
  const [teacherFeedbackStats, setTeacherFeedbackStats] = useState({ pending: 0 });
  const [staffFeedbackStats, setStaffFeedbackStats] = useState({ pending: 0 });
  const [activeAdminTab, setActiveAdminTab] = useState('urgent');
  const [seenNotifications, setSeenNotifications] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('adminSeenNotifications') || '{}');
    } catch (error) {
      return {};
    }
  });

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Admin management state (for super admin)
  const [showAdminManagement, setShowAdminManagement] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'management_admin'
  });
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [adminPasswordForm, setAdminPasswordForm] = useState({
    useDefaultPassword: true,
    newPassword: '',
    confirmPassword: '',
    forcePasswordChange: true
  });
  const [adminPasswordMessage, setAdminPasswordMessage] = useState(null);

  // Security user management state
  const [showSecurityManagement, setShowSecurityManagement] = useState(false);
  const [securityUsers, setSecurityUsers] = useState([]);
  const [securitySearch, setSecuritySearch] = useState('');
  const [newSecurityForm, setNewSecurityForm] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Permission management state
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  // Admin card assignment state
  const [showAdminCardModal, setShowAdminCardModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userType, setUserType] = useState(''); // 'teacher' or 'staff'
  const [availableAdminCards, setAvailableAdminCards] = useState([]);
  const [assignedAdminCards, setAssignedAdminCards] = useState([]);

  // Real-time overview state
  const [realtimeStats, setRealtimeStats] = useState({
    // Notification counts
    pendingApprovals: 0,
    studentFeedbackPending: 0,
    teacherFeedbackPending: 0,
    staffFeedbackPending: 0,
    expenseRequestsPending: 0,
    pendingEnrollments: 0,
    unreadMessages: 0,

    // Totals
    totalStudents: 0,
    totalTeachers: 0,
    totalStaff: 0,
    totalClasses: 0,
    totalSubjects: 0,
    totalLessons: 0,
    totalExams: 0,
    totalAnnouncements: 0,
    totalGalleryItems: 0,
    totalVideos: 0,

    // Performance metrics
    attendanceRate: 0,
    feeCollectionRate: 0,
    averagePerformance: 0,

    // Detailed data
    classDistribution: [],
    subjectPerformance: [],
    attendanceTrends: [],
    feeCollectionTrends: [],

    lastUpdated: null
  });

  // Message stats state
  const [messageStats, setMessageStats] = useState({
    total: 0,
    unread: 0,
    pending: 0,
    replied: 0
  });

  // Class management state
  const [showManageClasses, setShowManageClasses] = useState(false);





  useEffect(() => {
    fetchClasses();
    // Load approval requests and records for all admins, feedback stats for Super Admin
    if (currentAdmin.role) {
      loadApprovalRequests();
      loadRequestStats(); // Load expense request stats
      loadMessageStats(); // Load message stats for notification badge
      if (currentAdmin.role === 'super_admin') {
        loadFeedbackStats();
      }
      // Load teacher and staff feedback stats for all admins
      loadTeacherFeedbackStats();
      loadStaffFeedbackStats();
    }
  }, []);

  // Real-time updates effect
  useEffect(() => {
    // Initial load
    loadRealtimeStats();

    // Set up polling every 15 seconds
    const interval = setInterval(() => {
      loadRealtimeStats();
    }, 15000); // 15 seconds

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);



  const loadAdminUsers = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/users`, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const users = await response.json();
        setAdminUsers(users);
      }
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
  };

  const loadApprovalRequests = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      // Use different endpoints for different admin roles
      const endpoint = adminUser.role === 'super_admin'
        ? `${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests`  // All requests for super admin
        : `${API_ENDPOINTS.BASE_URL}/api/admin/approval/my-requests`; // Only user's requests

      const response = await fetch(endpoint, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const requests = await response.json();
        setApprovalRequests(requests);
      }
    } catch (error) {
      console.error('Error loading approval requests:', error);
    }
  };



  const loadFeedbackStats = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/feedback/stats`, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const stats = await response.json();
        setFeedbackStats(stats);
      }
    } catch (error) {
      console.error('Error loading feedback stats:', error);
    }
  };

  const loadTeacherFeedbackStats = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/feedback/all`, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const feedbackData = await response.json();
        const pendingCount = feedbackData.filter(item => item.status === 'pending').length;
        setTeacherFeedbackStats({ pending: pendingCount });
      }
    } catch (error) {
      console.error('Error loading teacher feedback stats:', error);
    }
  };

  const loadStaffFeedbackStats = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/feedback/all`, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const feedbackData = await response.json();
        const pendingCount = feedbackData.filter(item => item.status === 'pending').length;
        setStaffFeedbackStats({ pending: pendingCount });
      }
    } catch (error) {
      console.error('Error loading staff feedback stats:', error);
    }
  };

  const loadRequestStats = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/expenses/requests/stats`);
      if (response.ok) {
        const stats = await response.json();
        setRequestStats(stats);
      }
    } catch (error) {
      console.error('Error loading request stats:', error);
    }
  };

  const loadMessageStats = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      if (adminUser.email) {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/messages/admin/stats`, {
          headers: { 'admin-email': adminUser.email }
        });
        if (response.ok) {
          const stats = await response.json();
          setMessageStats(stats);
        }
      }
    } catch (error) {
      console.error('Error loading message stats:', error);
    }
  };



  const loadRealtimeStats = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

      // Fetch all data in parallel for efficiency
      const [
        approvalRequestsRes,
        studentFeedbackRes,
        teacherFeedbackRes,
        staffFeedbackRes,
        expenseStatsRes,
        studentsRes,
        teachersRes,
        staffRes,
        classesRes,
        subjectsRes,
        lessonsRes,
        midtermExamsRes,
        finalExamsRes,
        announcementsRes,
        galleryRes,
        videosRes,
        attendanceRes,
        paymentsRes,
        progressRes
      ] = await Promise.allSettled([
        fetch(adminUser.role === 'super_admin'
          ? `${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests`
          : `${API_ENDPOINTS.BASE_URL}/api/admin/approval/my-requests`, {
          headers: { 'admin-email': adminUser.email }
        }),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/feedback/stats`, {
          headers: { 'admin-email': adminUser.email }
        }),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/feedback/all`, {
          headers: { 'admin-email': adminUser.email }
        }),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/feedback/all`, {
          headers: { 'admin-email': adminUser.email }
        }),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/expenses/requests/stats`),
        fetch(`${API_ENDPOINTS.STUDENTS}`),
        fetch(`${API_ENDPOINTS.TEACHERS}`),
        fetch(`${API_ENDPOINTS.STAFF}`),
        fetch(`${API_ENDPOINTS.CLASSES}`),
        fetch(`${API_ENDPOINTS.SUBJECTS}`),
        fetch(`${API_ENDPOINTS.LESSONS}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/Midterm`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/exams/Final`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/announcements`),
        fetch(`${API_ENDPOINTS.GALLERY}`),
        fetch(`${API_ENDPOINTS.VIDEOS}`),
        getAllAttendance(),
        getAllPayments(),
        getAllProgress()
      ]);

      // Parse all responses once to avoid body stream already read error
      const approvalRequestsData = approvalRequestsRes.status === 'fulfilled' && approvalRequestsRes.value.ok
        ? await approvalRequestsRes.value.json()
        : [];

      const studentFeedbackData = studentFeedbackRes.status === 'fulfilled' && studentFeedbackRes.value.ok
        ? await studentFeedbackRes.value.json()
        : {};

      const teacherFeedbackData = teacherFeedbackRes.status === 'fulfilled' && teacherFeedbackRes.value.ok
        ? await teacherFeedbackRes.value.json()
        : [];

      const staffFeedbackData = staffFeedbackRes.status === 'fulfilled' && staffFeedbackRes.value.ok
        ? await staffFeedbackRes.value.json()
        : [];

      const expenseStatsData = expenseStatsRes.status === 'fulfilled' && expenseStatsRes.value.ok
        ? await expenseStatsRes.value.json()
        : {};

      const studentsData = studentsRes.status === 'fulfilled' && studentsRes.value.ok
        ? await studentsRes.value.json()
        : [];

      const teachersData = teachersRes.status === 'fulfilled' && teachersRes.value.ok
        ? await teachersRes.value.json()
        : [];

      const staffData = staffRes.status === 'fulfilled' && staffRes.value.ok
        ? await staffRes.value.json()
        : [];

      const classesData = classesRes.status === 'fulfilled' && classesRes.value.ok
        ? await classesRes.value.json()
        : [];

      const subjectsData = subjectsRes.status === 'fulfilled' && subjectsRes.value.ok
        ? await subjectsRes.value.json()
        : [];

      const lessonsData = lessonsRes.status === 'fulfilled' && lessonsRes.value.ok
        ? await lessonsRes.value.json()
        : [];

      const midtermExamsData = midtermExamsRes.status === 'fulfilled' && midtermExamsRes.value.ok
        ? await midtermExamsRes.value.json()
        : [];

      const finalExamsData = finalExamsRes.status === 'fulfilled' && finalExamsRes.value.ok
        ? await finalExamsRes.value.json()
        : [];

      const announcementsData = announcementsRes.status === 'fulfilled' && announcementsRes.value.ok
        ? await announcementsRes.value.json()
        : [];

      const galleryData = galleryRes.status === 'fulfilled' && galleryRes.value.ok
        ? await galleryRes.value.json()
        : [];

      const videosData = videosRes.status === 'fulfilled' && videosRes.value.ok
        ? await videosRes.value.json()
        : [];

      // Process notification counts
      const pendingApprovals = approvalRequestsData.filter(r => r.status === 'pending').length;
      const studentFeedbackPending = studentFeedbackData.pending || 0;
      const teacherFeedbackPending = teacherFeedbackData.filter(item => item.status === 'pending').length;
      const staffFeedbackPending = staffFeedbackData.filter(item => item.status === 'pending').length;
      const expenseRequestsPending = expenseStatsData.pending || 0;

      // Process unread messages
      let unreadMessages = 0;
      try {
        const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
        if (adminUser.email) {
          // For now, we'll set it to 0 and load it separately
          // This will be updated in the next polling cycle
          unreadMessages = 0;
        }
      } catch (error) {
        unreadMessages = 0;
      }

      // Process totals
      const totalStudents = studentsData.length;
      const totalTeachers = teachersData.length;
      const totalStaff = staffData.length;
      const totalClasses = classesData.length;
      const totalSubjects = subjectsData.length;
      const totalLessons = lessonsData.length;
      const totalExams = midtermExamsData.length + finalExamsData.length;
      const totalAnnouncements = announcementsData.length;
      const totalGalleryItems = galleryData.length;
      const totalVideos = videosData.length;

      // Calculate performance metrics
      const attendance = attendanceRes.status === 'fulfilled' ? attendanceRes.value : [];
      const payments = paymentsRes.status === 'fulfilled' ? paymentsRes.value : [];
      const progress = progressRes.status === 'fulfilled' ? progressRes.value : [];

      const attendanceRate = attendance.length > 0
        ? Math.round((attendance.filter(a => a.status === 'present').length / attendance.length) * 100)
        : 0;

      const feeCollectionRate = payments.length > 0
        ? Math.round((payments.filter(p => p.status === 'completed').length / payments.length) * 100)
        : 0;

      const averagePerformance = progress.length > 0
        ? Math.round((progress.reduce((sum, p) => sum + (p.score || 0), 0) / progress.length) * 100) / 100
        : 0;

      // Class distribution
      const classDistribution = classesData.map(cls => ({
        name: cls,
        count: studentsData.filter(s => s.className === cls).length
      }));

      // Subject performance (simplified)
      const subjectStats = {};
      progress.forEach(item => {
        if (!subjectStats[item.subject]) {
          subjectStats[item.subject] = { subject: item.subject, totalScores: 0, count: 0 };
        }
        subjectStats[item.subject].totalScores += item.score || 0;
        subjectStats[item.subject].count++;
      });
      const subjectPerformance = Object.values(subjectStats).map(stats => ({
        subject: stats.subject,
        averageScore: stats.count > 0 ? Math.round((stats.totalScores / stats.count) * 100) / 100 : 0
      })).sort((a, b) => b.averageScore - a.averageScore).slice(0, 5);

      // Trends (simplified - last 7 days)
      const attendanceTrends = [];
      const feeCollectionTrends = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];

        const dayAttendance = attendance.filter(a => a.date === dateStr);
        const dayPayments = payments.filter(p => {
          const paymentDate = p.date || p.paymentDate || p.createdAt || p.initiatedAt;
          return typeof paymentDate === 'string' && paymentDate.startsWith(dateStr);
        });

        attendanceTrends.push({
          date: dateStr,
          rate: dayAttendance.length > 0 ? Math.round((dayAttendance.filter(a => a.status === 'present').length / dayAttendance.length) * 100) : 0
        });

        feeCollectionTrends.push({
          date: dateStr,
          rate: dayPayments.length > 0 ? Math.round((dayPayments.filter(p => p.status === 'completed').length / dayPayments.length) * 100) : 0
        });
      }

      setRealtimeStats({
        pendingApprovals,
        studentFeedbackPending,
        teacherFeedbackPending,
        staffFeedbackPending,
        expenseRequestsPending,
        unreadMessages,
        totalStudents,
        totalTeachers,
        totalStaff,
        totalClasses,
        totalSubjects,
        totalLessons,
        totalExams,
        totalAnnouncements,
        totalGalleryItems,
        totalVideos,
        attendanceRate,
        feeCollectionRate,
        averagePerformance,
        classDistribution,
        subjectPerformance,
        attendanceTrends,
        feeCollectionTrends,
        lastUpdated: new Date()
      });

    } catch (error) {
      console.error('Error loading realtime stats:', error);
    }
  };

  // Helper functions for attendance, payments, and progress
  const getAllAttendance = async () => {
    try {
      const students = await fetch(`${API_ENDPOINTS.STUDENTS}`).then(r => r.json());
      const allAttendance = [];

      for (const student of students) {
        try {
          const attendance = await fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/${student.username}`).then(r => r.json());
          allAttendance.push(...attendance);
        } catch (error) {
          console.error(`Error fetching attendance for ${student.username}:`, error);
        }
      }

      return allAttendance;
    } catch (error) {
      console.error('Error getting all attendance:', error);
      return [];
    }
  };

  const getAllPayments = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/payments`);
      if (!response.ok) {
        console.error('Error fetching payments:', response.status);
        return [];
      }
      const allPayments = await response.json();
      return allPayments;
    } catch (error) {
      console.error('Error getting all payments:', error);
      return [];
    }
  };

  const getAllProgress = async () => {
    try {
      const students = await fetch(`${API_ENDPOINTS.STUDENTS}`).then(r => r.json());
      const allProgress = [];

      for (const student of students) {
        try {
          const progress = await fetch(`${API_ENDPOINTS.STUDENTS}/progress/${student.username}`).then(r => r.json());
          allProgress.push(...progress);
        } catch (error) {
          console.error(`Error fetching progress for ${student.username}:`, error);
        }
      }

      return allProgress;
    } catch (error) {
      console.error('Error getting all progress:', error);
      return [];
    }
  };

  const loadSecurityUsers = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/security-users`, {
        headers: {
          'admin-email': adminUser.email
        }
      });
      if (response.ok) {
        const users = await response.json();
        setSecurityUsers(users);
      }
    } catch (error) {
      console.error('Error loading security users:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.CLASSES}`);
      if (!response.ok) throw new Error('Failed to fetch classes');
      const data = await response.json();
      setClasses(data);
      if (data.length > 0) setSelectedClass(data[0]);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const addClass = async () => {
    const trimmedClass = newClass.trim();
    if (!trimmedClass) return alert('Please enter a class name.');
    if (classes.includes(trimmedClass)) return alert('This class already exists!');

    try {
      const response = await fetch(`${API_ENDPOINTS.CLASSES}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ className: trimmedClass }),
      });
      if (!response.ok) throw new Error('Failed to add class');
      const updatedClasses = await response.json();
      setClasses(updatedClasses);
      setNewClass('');
      setSelectedClass(updatedClasses[0] || '');
    } catch (error) {
      console.error('Error adding class:', error);
      alert('Failed to add class. Please try again.');
    }
  };

  const deleteClass = async () => {
    if (!selectedClass) return alert('Please select a class to delete.');
    if (!window.confirm(`Are you sure you want to delete "${selectedClass}"?`)) return;

    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/api/classes/${encodeURIComponent(selectedClass)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Failed to delete class');
      const updatedClasses = await response.json();
      setClasses(updatedClasses);
      setSelectedClass(updatedClasses[0] || '');
    } catch (error) {
      console.error('Error deleting class:', error);
      alert('Failed to delete class. Please try again.');
    }
  };

  const loadAdminCardData = async () => {
    try {
      // Load available admin cards
      const availableResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/teachers/admin-cards/available`);
      if (availableResponse.ok) {
        const availableData = await availableResponse.json();
        setAvailableAdminCards(availableData.adminCards);
      }
    } catch (error) {
      console.error('Error loading admin cards:', error);
    }
  };



  // Define adminOptions dynamically to ensure notification counts are current
  const adminOptions = [
    {
      title: 'Approval Management',
      description: 'Review pending requests and view approval history.',
      path: '/admin/approval-management',
      requiredPermission: 'approvals.read',
      hasNotification: true,
      notificationCount: approvalRequests.filter(r => r.status === 'pending').length,
      tooltip: 'Click to access the approval management system. Review expense requests, staff applications, and other pending approvals. You can approve or reject requests, view detailed information, and track approval history. Requires approval permissions to access.'
    },
    {
      title: 'Feedback Management',
      description: 'Review and respond to student feedback.',
      path: '/admin/feedback',
      requiredPermission: 'feedback.read',
      hasNotification: true,
      notificationCount: feedbackStats.pending || 0,
      tooltip: 'Access student feedback and complaints. Read messages, respond to concerns, mark as resolved, and track feedback trends. Use this to improve school services and address student issues promptly.'
    },
    {
      title: 'Fee Management',
      description: 'Manage fee structures, track payments, and generate financial reports.',
      path: '/admin/fee-management',
      requiredPermission: 'fees.manage',
      tooltip: 'Manage school fee structures, payment tracking, and financial reporting. Set fee amounts, view payment history, generate invoices, track outstanding balances, and create financial reports for different time periods.'
    },
    {
      title: 'Parents Management',
      description: 'Create parent accounts, link children, and move fee payments to parents.',
      path: '/admin/parents',
      requiredPermission: 'students.manage',
      tooltip: 'Manage parent portal access, connect parents to their children, and keep payment responsibility with parent accounts instead of student accounts.'
    },
    {
      title: 'Classroom Cameras',
      description: 'Manage classroom camera access and on-demand viewing.',
      path: '/admin/cameras',
      requiredPermission: 'camera.manage',
      tooltip: 'Classroom camera management'
    },
    {
      title: 'Analytics Dashboard',
      description: 'View school-wide performance metrics and insights.',
      path: '/admin/analytics',
      requiredPermission: 'analytics.read',
      tooltip: 'View comprehensive school analytics including attendance rates, academic performance, fee collection, and student progress trends. Use charts and graphs to identify areas needing improvement and track school performance over time.'
    },
    {
      title: 'General Attendance Dashboard',
      description: 'Unified view of attendance across all students, teachers, and staff.',
      path: '/admin/general-attendance-dashboard',
      requiredPermission: 'attendance.manage',
      tooltip: 'Monitor attendance for all students, teachers, and staff members. View daily attendance records, identify patterns, generate reports, and track attendance trends. Mark attendance when needed and manage attendance policies.'
    },
    {
      title: 'Progress Management',
      description: 'Update grades and manage student achievements.',
      path: '/admin/progress-management',
      requiredPermission: 'progress.manage',
      tooltip: 'Manage student academic progress and grades. Update scores for assignments and exams, track student performance, generate progress reports, and identify students who need additional support or recognition.'
    },
    {
      title: 'Register Student',
      description: 'Add a new student to the system.',
      path: '/admin/register-student',
      requiredPermission: 'students.create',
      tooltip: 'Register new students in the system. Enter student details, assign class and subjects, set up login credentials, and complete the enrollment process. Ensure all required information is provided for proper student records.'
    },
    {
      title: 'Manage Students',
      description: 'Edit or remove student records.',
      path: '/admin/manage-students',
      requiredPermission: 'students.manage',
      tooltip: 'View and edit student information, manage enrollments, update profiles, and handle student transfers or withdrawals. Search students by name, class, or ID to quickly access and modify student records.'
    },
    {
      title: 'Manage Subjects & Lessons',
      description: 'Handle subjects and lesson materials.',
      path: '/admin/manage-subjects',
      requiredPermission: 'subjects.manage',
      tooltip: 'Create and organize subjects, upload lesson materials, assign subjects to classes and teachers. Manage curriculum content, update learning materials, and ensure all subjects have appropriate lesson plans and resources.'
    },
    {
      title: 'Manage Midterm Exams',
      description: 'Create and review midterm exams.',
      path: '/admin/midterm-exams',
      requiredPermission: 'exams.midterm.manage',
      tooltip: 'Create midterm examinations, set questions, schedule exam dates, and review results. Assign exams to specific classes and subjects, manage exam settings, and analyze midterm performance across the school.'
    },
    {
      title: 'Manage Final Exams',
      description: 'Set up final term exams.',
      path: '/admin/final-exams',
      requiredPermission: 'exams.final.manage',
      tooltip: 'Organize final term examinations, create exam schedules, assign invigilators, and manage grading. Set up comprehensive final assessments, track exam completion, and generate final grade reports.'
    },
    {
      title: 'Timetable Management',
      description: 'Create and manage class schedules and teacher assignments.',
      path: '/admin/timetable-management',
      requiredPermission: 'timetable.manage',
      tooltip: 'Create weekly class schedules, assign teachers to subjects and periods, manage classroom allocations, and handle timetable conflicts. Generate printable timetables and ensure optimal use of school resources.'
    },
    {
      title: 'Teacher Management',
      description: 'Manage teachers, assignments, attendance, and salaries.',
      path: '/admin/teacher-management',
      requiredPermission: 'teachers.manage',
      hasNotification: true,
      notificationCount: teacherFeedbackStats.pending || 0,
      tooltip: 'Manage teacher information, assignments, attendance tracking, and salary administration. Assign teachers to classes and subjects, monitor teacher performance, handle teacher feedback, and process salary payments.'
    },
    {
      title: 'Non-Academic Staff Management',
      description: 'Manage non-academic staff, attendance, salaries, and requests.',
      path: '/admin/staff-management',
      requiredPermission: 'staff.manage',
      hasNotification: true,
      notificationCount: staffFeedbackStats.pending || 0,
      tooltip: 'Oversee non-teaching staff including administrative, maintenance, and support personnel. Manage staff records, track attendance, handle salary payments, and process staff requests and feedback.'
    },
    {
      title: 'Salary Payment System',
      description: 'Process salary payments for all employees via Flutterwave - individual or batch payments.',
      path: '/admin/salary-management',
      requiredPermission: 'salaries.manage',
      tooltip: 'Process salary payments for teachers and staff through Flutterwave integration. Make individual payments or batch payments, track payment history, generate payslips, and manage salary schedules and deductions.'
    },
    {
      title: 'Expense Management',
      description: 'Track and manage all school expenses, budgets, and approvals.',
      path: '/admin/expense-management',
      requiredPermission: 'expenses.manage',
      hasNotification: true,
      notificationCount: requestStats.pending || 0,
      tooltip: 'Monitor school expenses, manage budgets, and approve expense requests. Track spending by category, generate expense reports, set budget limits, and ensure all expenditures are properly approved and documented.'
    },
    {
      title: 'Submit Expense Request',
      description: 'Create and submit new expense requests for approval.',
      path: '/admin/submit-expense-request',
      requiredPermission: 'expenses.manage',
      tooltip: 'Submit new expense requests for approval. Provide detailed justification, attach receipts if available, specify expense category and amount. Track approval status and view expense request history.'
    },
    {
      title: 'Security Management',
      description: 'Manage security codes and monitor attendance verification.',
      path: '/admin/security-management',
      requiredPermission: 'security.manage',
      tooltip: 'Manage security codes for parent verification, monitor attendance security measures, and oversee security personnel. Generate and distribute security codes, track verification logs, and ensure secure attendance processes.'
    },
    {
      title: 'Drop-off / Pickup Verification',
      description: 'Verify a child\'s drop-off or pickup security code.',
      path: '/security',
      requiredPermission: 'security.manage',
      tooltip: 'Open the school gate code verification page.'
    },
    {
      title: 'Security User Management',
      description: 'Create and manage security personnel accounts.',
      type: 'security_user_management',
      requiredPermission: 'security.users.manage',
      tooltip: 'Create and manage accounts for security personnel. Set up login credentials, assign security roles, and control access to security-related functions. Monitor security user activity and manage account permissions.'
    },
    {
      title: 'School Announcements',
      description: 'Post news and updates for students.',
      path: '/admin/news',
      requiredPermission: 'announcements.manage',
      tooltip: 'Create and publish school announcements, news, and important updates. Target specific audiences (all students, specific classes), schedule announcements, and track announcement read status and engagement.'
    },
    {
      title: 'Admin Management',
      description: 'Create and manage administrator accounts and permissions.',
      type: 'admin_management',
      requiredPermission: 'admin.users.manage',
      tooltip: 'Create new administrator accounts, manage user roles and permissions. Assign specific access rights, control system privileges, and maintain administrative user accounts. Available only to super administrators.'
    },
    {
      title: 'Gallery & Social Media Management',
      description: 'Upload and manage gallery media, monitor social engagement.',
      path: '/admin/gallery-management',
      requiredPermission: 'gallery.manage',
      tooltip: 'Manage school photo/video gallery, upload media content, and monitor social media engagement. Organize media by events, create albums, approve content for public display, and track social media metrics.'
    },
    {
      title: 'Enrollment Management',
      description: 'Review and manage student enrollment applications, approve/reject submissions.',
      type: 'enrollment_management',
      requiredPermission: 'enrollments.manage',
      hasNotification: true,
      notificationCount: 0,
      tooltip: 'Review student enrollment applications, verify submitted information, and approve or reject enrollment requests. Process new student registrations, manage enrollment status, and handle enrollment-related communications.'
    },
    {
      title: 'Manage Classes',
      description: 'Add, view, and delete class names.',
      type: 'manage_classes',
      requiredPermission: 'classes.manage',
      tooltip: 'Add new class names to the system, view existing classes, and remove unused classes. Ensure class names follow school naming conventions and maintain organized class structure for the academic year.'
    },
    {
      title: 'Assign Admin Cards',
      description: 'Assign administrative functions to teachers and staff members.',
      type: 'admin_card_assignment',
      requiredPermission: 'admin.manage',
      tooltip: 'Assign administrative privileges and management cards to teachers and staff. Grant specific administrative functions like attendance management, fee collection, or student record access to appropriate personnel.'
    },
    {
      title: 'Contact Message Management',
      description: 'View and respond to contact messages from users and website visitors.',
      path: '/admin/messaging',
      requiredPermission: 'messages.manage',
      hasNotification: messageStats.unread > 0,
      notificationCount: messageStats.unread,
      tooltip: 'View and respond to messages from parents, students, and website visitors. Read inquiries, provide responses, mark messages as resolved, and maintain communication history. Monitor message volume and response times.'
    },
    {
      title: 'Email Management',
      description: 'Configure email settings, send bulk emails, and manage email templates.',
      path: '/admin/email-management',
      requiredPermission: 'admin.update',
      tooltip: 'Configure SMTP settings for your mail server, SendGrid, or Gmail. Send test emails, manage bulk communications, customize email templates, and monitor email delivery statistics and logs.'
    },
    {
      title: 'Video Call Settings',
      description: 'Control which students, teachers, and parents can call each other.',
      path: '/admin/video-call-settings',
      requiredPermission: 'admin.update',
      tooltip: 'Set school-wide video call permissions by caller and recipient group. Parent calls remain restricted to children linked to their own account.'
    },
    {
      title: 'Ad Redirect Management',
      description: 'Manage the /ad redirect URL and view redirect statistics.',
      path: '/admin/ad-management',
      requiredPermission: 'admin.update',
      tooltip: 'Set and manage the redirect URL for the /ad page. View redirect counts and manage ad redirection settings centrally from the backend.'
    },
    {
      title: 'Tracking',
      description: 'Track website visitors, campaign sources, page actions, and staff account activity.',
      path: '/admin/tracking',
      requiredPermission: 'analytics.read',
      tooltip: 'View high-level tracking for all visitors, ads traffic, page views, clicks, form submits, API activity, and logged-in staff/admin account activity. Sensitive form values are not recorded.'
    },
    {
      title: 'Real-time Dashboard Overview',
      description: 'Comprehensive real-time view of all school metrics, notifications, and performance data.',
      type: 'realtime_overview',
      requiredPermission: 'analytics.read',
      hasNotification: (realtimeStats.pendingApprovals + realtimeStats.studentFeedbackPending + realtimeStats.teacherFeedbackPending + realtimeStats.staffFeedbackPending + realtimeStats.expenseRequestsPending + realtimeStats.unreadMessages) > 0,
      notificationCount: realtimeStats.pendingApprovals + realtimeStats.studentFeedbackPending + realtimeStats.teacherFeedbackPending + realtimeStats.staffFeedbackPending + realtimeStats.expenseRequestsPending + realtimeStats.unreadMessages,
      tooltip: 'View comprehensive real-time school overview with live metrics, pending notifications, and performance indicators. Monitor all critical school functions, identify urgent issues, and track overall school health at a glance.'
    }
  ];

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
  setPasswordForm(prev => ({
    ...prev,
    currentPassword: name === 'currentPassword' ? value : prev.currentPassword,
    newPassword: name === 'newPassword' ? value : prev.newPassword,
    confirmPassword: name === 'confirmPassword' ? value : prev.confirmPassword,
  }));
  };

  const handlePasswordChange = async () => {
    // Reset messages
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      // Get admin info from localStorage
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: adminUser.email,
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const result = await response.json();

      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setShowPasswordChange(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(result.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('Failed to change password. Please try again.');
    }
  };

  const handleLogout = async () => {
    await fetch(`${API_ENDPOINTS.BASE_URL}/api/session/logout`, {method:'POST',credentials:'include'}).catch(()=>{});
    // Clear admin session data
    localStorage.removeItem('adminUser');
    // Redirect to admin login
    navigate('/admin');
  };

  // Permission-based access check
  const hasPermission = (permission) => {
    if (!currentAdmin) return false;
    // Super admin has wildcard permission
    if (currentAdmin.permissions && currentAdmin.permissions.includes('*')) return true;
    return currentAdmin.permissions && currentAdmin.permissions.includes(permission);
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { text: 'Super Admin', class: 'bg-red-100 text-red-800' },
      management_admin: { text: 'Management Admin', class: 'bg-yellow-100 text-yellow-800' },
      principal: { text: 'Principal', class: 'bg-orange-100 text-orange-800' },
      teacher: { text: 'Teacher', class: 'bg-green-100 text-green-800' },
      staff: { text: 'Staff', class: 'bg-purple-100 text-purple-800' }
    };
    return badges[role] || { text: role, class: 'bg-gray-100 text-gray-800' };
  };

  const getDefaultPasswordForRole = (role) => {
    if (role === 'teacher') return 'teacher123';
    if (role === 'staff') return 'staff123';
    return '123456';
  };

  const roleSupportsForcedChange = (role) => ['teacher', 'staff'].includes(role);

  const openAdminPasswordModal = (admin) => {
    setPasswordUser(admin);
    setAdminPasswordForm({
      useDefaultPassword: true,
      newPassword: '',
      confirmPassword: '',
      forcePasswordChange: roleSupportsForcedChange(admin.role)
    });
    setAdminPasswordMessage(null);
    setShowAdminPasswordModal(true);
  };

  const closeAdminPasswordModal = () => {
    setShowAdminPasswordModal(false);
    setPasswordUser(null);
    setAdminPasswordMessage(null);
    setAdminPasswordForm({
      useDefaultPassword: true,
      newPassword: '',
      confirmPassword: '',
      forcePasswordChange: true
    });
  };

  const resetAdminUserPassword = async (event) => {
    event.preventDefault();
    if (!passwordUser) return;

    const passwordToSet = adminPasswordForm.useDefaultPassword
      ? getDefaultPasswordForRole(passwordUser.role)
      : adminPasswordForm.newPassword;

    if (!passwordToSet || passwordToSet.length < 6) {
      setAdminPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters long.' });
      return;
    }

    if (!adminPasswordForm.useDefaultPassword && adminPasswordForm.newPassword !== adminPasswordForm.confirmPassword) {
      setAdminPasswordMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }

    if (!window.confirm(`Reset password for ${passwordUser.name}?`)) return;

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/users/${passwordUser.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({
          newPassword: passwordToSet,
          useDefaultPassword: adminPasswordForm.useDefaultPassword,
          forcePasswordChange: adminPasswordForm.useDefaultPassword && adminPasswordForm.forcePasswordChange
        })
      });
      const result = await response.json();

      if (!response.ok) {
        setAdminPasswordMessage({ type: 'error', text: result.error || 'Failed to reset password.' });
        return;
      }

      setAdminPasswordMessage({
        type: 'success',
        text: `Password reset successfully. Temporary password: ${result.temporaryPassword}`
      });
      loadAdminUsers();
      setAdminPasswordForm(current => ({ ...current, newPassword: '', confirmPassword: '' }));
    } catch (error) {
      console.error('Error resetting admin user password:', error);
      setAdminPasswordMessage({ type: 'error', text: 'Failed to reset password. Please try again.' });
    }
  };

  const adminCardPalette = [
    { color: '#0ea5e9', soft: '#e0f2fe' },
    { color: '#f97316', soft: '#ffedd5' },
    { color: '#10b981', soft: '#d1fae5' },
    { color: '#8b5cf6', soft: '#ede9fe' },
    { color: '#ec4899', soft: '#fce7f3' },
    { color: '#f59e0b', soft: '#fef3c7' }
  ];

  const getAdminCardTheme = (index) => adminCardPalette[index % adminCardPalette.length];
  const notificationKeyFor = (option) => `${currentAdmin?.email || 'admin'}:${option.path || option.type || option.title}`;
  const getVisibleNotificationCount = (option) => {
    const count = option.hasNotification ? Number(option.notificationCount || 0) : 0;
    if (count <= 0) return 0;
    return count > Number(seenNotifications[notificationKeyFor(option)] || 0) ? count : 0;
  };
  const markNotificationSeen = (option) => {
    const count = Number(option.notificationCount || 0);
    if (!option.hasNotification || count <= 0) return;

    const nextSeen = {
      ...seenNotifications,
      [notificationKeyFor(option)]: count
    };
    setSeenNotifications(nextSeen);
    localStorage.setItem('adminSeenNotifications', JSON.stringify(nextSeen));
  };
  const openAdminOption = (option) => {
    markNotificationSeen(option);
    if (option.type === 'admin_management') {
      setShowAdminManagement(true);
      loadAdminUsers();
    } else if (option.type === 'security_user_management') {
      setShowSecurityManagement(true);
      loadSecurityUsers();
    } else if (option.type === 'manage_classes') {
      setShowManageClasses(true);
    } else if (option.type === 'admin_card_assignment') {
      setShowAdminCardModal(true);
      loadAdminCardData();
    } else if (option.type === 'enrollment_management') {
      navigate('/admin/enrollment-management');
    } else if (option.type === 'realtime_overview') {
      navigate('/admin/realtime-overview');
    } else {
      navigate(option.path);
    }
  };
  const adminTabs = [
    { id: 'urgent', label: 'Notifications', match: (option) => getVisibleNotificationCount(option) > 0 },
    { id: 'cameras', label: 'Cameras', match: (option) => option.path === '/admin/cameras' },
    { id: 'people', label: 'People', match: (option) => ['Register Student', 'Manage Students', 'Teacher Management', 'Non-Academic Staff Management', 'Parents Management', 'Enrollment Management', 'Admin Management', 'Security User Management', 'Assign Admin Cards'].includes(option.title) },
    { id: 'academic', label: 'Academic', match: (option) => ['Manage Classes', 'Manage Subjects & Lessons', 'Manage Midterm Exams', 'Manage Final Exams', 'Timetable Management', 'Progress Management', 'General Attendance Dashboard'].includes(option.title) },
    { id: 'finance', label: 'Finance', match: (option) => ['Fee Management', 'Expense Management', 'Submit Expense Request', 'Salary Payment System'].includes(option.title) },
    { id: 'communication', label: 'Communication', match: (option) => ['Feedback Management', 'Contact Message Management', 'School Announcements', 'Email Management', 'Video Call Settings', 'Gallery & Social Media Management', 'Ad Redirect Management'].includes(option.title) },
    { id: 'insights', label: 'Insights', match: (option) => ['Analytics Dashboard', 'Real-time Dashboard Overview', 'Security Management', 'Drop-off / Pickup Verification'].includes(option.title) },
    { id: 'tracking', label: 'Tracking', match: (option) => ['Tracking'].includes(option.title) },
    { id: 'all', label: 'All Pages', match: () => true }
  ];
  const filteredSecurityUsers = securityUsers.filter((user) => {
    const query = securitySearch.toLowerCase().trim();
    if (!query) return true;
    return [user.name, user.email, user.id]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });
  const activeSecurityUsers = securityUsers.filter((user) => user.active).length;
  const getOptionTooltip = (option) => option.tooltip || `${option.title}: ${option.description} Open this card to manage the related records, review status, make updates, and use the tools available for this function.`;

  return (
    <div className="app-vibrant-shell p-4 md:p-8">
      <div className="app-vibrant-header flex flex-col lg:flex-row justify-between lg:items-center gap-4 mb-8 p-5 md:p-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-950">Admin Dashboard</h1>
          {currentAdmin && (
            <div className="flex items-center mt-2">
              <span className="text-gray-600 mr-2">Welcome, {currentAdmin.name}</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadge(currentAdmin.role).class}`}>
                {getRoleBadge(currentAdmin.role).text}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowPasswordChange(true)}
            className="bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 transition duration-300 shadow-md"
          >
            Change Password
          </button>
          <DateTimeDisplay />
          <button
            onClick={handleLogout}
            className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition duration-300 shadow-md"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="mb-6 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {adminTabs.map((tab) => {
            const tabCount = adminOptions
              .filter(option => currentAdmin?.role === 'super_admin' || hasPermission(option.requiredPermission))
              .filter(tab.match).length;
            const isActive = activeAdminTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveAdminTab(tab.id)}
                className={`px-4 py-3 rounded-xl text-sm font-bold border transition shadow-sm ${
                  isActive
                    ? 'bg-blue-950 text-white border-blue-950'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-sky-300 hover:text-sky-700'
                }`}
              >
                {tab.label}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${isActive ? 'bg-white text-blue-950' : 'bg-slate-100 text-slate-600'}`}>
                  {tabCount}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Admin Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {adminOptions
          .filter(option => {
            if (currentAdmin?.role === 'super_admin') return true;
            return hasPermission(option.requiredPermission);
          })
          .filter(option => {
            const selectedTab = adminTabs.find(tab => tab.id === activeAdminTab) || adminTabs[0];
            return selectedTab.match(option);
          })
          .sort((a, b) => {
            // Sort notification cards to the top
            const aHasNotification = getVisibleNotificationCount(a) > 0;
            const bHasNotification = getVisibleNotificationCount(b) > 0;

            if (aHasNotification && !bHasNotification) return -1;
            if (!aHasNotification && bHasNotification) return 1;
            return 0; // Maintain original order within each group
          })
          .map((option, index) => {
            const theme = getAdminCardTheme(index);
            const visibleNotificationCount = getVisibleNotificationCount(option);
            return (
          <div
            key={index}
            onClick={() => openAdminOption(option)}
            className="app-vibrant-card rounded-2xl p-6 cursor-pointer transition duration-300 relative overflow-hidden"
            style={{ '--card-color': theme.color, '--card-soft': theme.soft }}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-start gap-3">
                <div className="app-vibrant-badge w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold shrink-0">
                  {option.title.split(' ').map(word => word[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{option.title}</h2>
                  <Tooltip content={getOptionTooltip(option)} position="top">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-800 text-sm font-black border border-sky-200 cursor-help" aria-label={`Information about ${option.title}`}>?</span>
                  </Tooltip>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {visibleNotificationCount > 0 && (
                  <div className="bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center animate-pulse">
                    {visibleNotificationCount}
                  </div>
                )}
                {option.type === 'approval_management' && (
                  <span className="text-2xl">Applications</span>
                )}
                {option.type === 'admin_management' && (
                  <span className="text-2xl">Students</span>
                )}
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed">{option.description}</p>
          </div>
            );
          })}
      </div>



      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Change Password</h3>

              {passwordError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                  {passwordSuccess}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter current password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handlePasswordChange}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                >
                  Change Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setPasswordForm({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Management Modal (Super Admin Only) */}
      {showAdminManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Admin User Management</h2>
                <button
                  onClick={() => setShowAdminManagement(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Add New Admin Form */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Create New Admin User</h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newAdminForm.email || !newAdminForm.password || !newAdminForm.name) {
                    alert('All fields are required');
                    return;
                  }

                  try {
                    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/users`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'admin-email': adminUser.email
                      },
                      body: JSON.stringify(newAdminForm)
                    });

                    if (response.ok) {
                      alert('Admin user created successfully!');
                      setNewAdminForm({ email: '', password: '', name: '', role: 'management_admin' });
                      loadAdminUsers(); // Refresh list
                    } else {
                      alert('Failed to create admin user');
                    }
                  } catch (error) {
                    console.error('Error creating admin:', error);
                    alert('Failed to create admin user');
                  }
                }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    placeholder="Email"
                    value={newAdminForm.email}
                    onChange={(e) => setNewAdminForm({...newAdminForm, email: e.target.value})}
                    className="border rounded px-3 py-2"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={newAdminForm.name}
                    onChange={(e) => setNewAdminForm({...newAdminForm, name: e.target.value})}
                    className="border rounded px-3 py-2"
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={newAdminForm.password}
                    onChange={(e) => setNewAdminForm({...newAdminForm, password: e.target.value})}
                    className="border rounded px-3 py-2"
                    required
                  />
                  <select
                    value={newAdminForm.role}
                    onChange={(e) => setNewAdminForm({...newAdminForm, role: e.target.value})}
                    className="border rounded px-3 py-2"
                  >
                    <option value="super_admin">Super Admin</option>
                    <option value="management_admin">Management Admin</option>
                    <option value="principal">Principal</option>
                    <option value="teacher">Teacher</option>
                  </select>
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
                    >
                      Create Admin User
                    </button>
                  </div>
                </form>
              </div>

              {/* Existing Admin Users */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Existing Admin Users</h3>
                {adminUsers.map((admin) => (
                  <div key={admin.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-bold text-gray-800">{admin.name}</h4>
                          <span className={`px-2 py-1 rounded text-xs ${getRoleBadge(admin.role).class}`}>
                            {getRoleBadge(admin.role).text}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${admin.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {admin.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">{admin.email}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
                          <span>Last login: {admin.lastLogin ? new Date(admin.lastLogin).toLocaleString() : 'Never'}</span>
                          <span>
                            Password: {admin.lastPasswordReset
                              ? `Reset ${new Date(admin.lastPasswordReset).toLocaleString()}`
                              : admin.lastPasswordChange
                              ? `Changed ${new Date(admin.lastPasswordChange).toLocaleString()}`
                              : 'No reset recorded'}
                          </span>
                        </div>

                        {/* Role Capabilities */}
                        <div className="bg-gray-50 rounded p-3 text-sm">
                          <h5 className="font-medium text-gray-700 mb-2">Capabilities:</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {admin.role === 'super_admin' && (
                              <>
                                {admin.permissions && admin.permissions.includes('*') ? (
                                  <>
                                    <div className="flex items-center space-x-2">
                                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                      <span>Full system access & admin management</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                      <span>Approve/reject deletion requests</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                      <span>Create & manage admin accounts</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                      <span>All student & academic data access</span>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="flex items-center space-x-2">
                                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                      <span>Limited super admin access</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                      <span>Custom permissions assigned</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                      <span>Role-based restrictions apply</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                      <span>Selective system access</span>
                                    </div>
                                  </>
                                )}
                              </>
                            )}

                            {admin.role === 'management_admin' && (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                                  <span>Student registration & management</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                                  <span>Fee structure & payment management</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                                  <span>Academic program management</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                                  <span>School announcements & feedback</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                  <span>Requires approval for deletions</span>
                                </div>
                              </>
                            )}

                            {admin.role === 'principal' && (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                  <span>Academic program oversight</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                  <span>Staff management (limited)</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                  <span>Student discipline management</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                  <span>School announcements</span>
                                </div>
                              </>
                            )}

                            {admin.role === 'teacher' && (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  <span>Class-specific student management</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  <span>Attendance marking</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  <span>Student progress tracking</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  <span>Limited reports for assigned classes</span>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Permissions List */}
                          {admin.permissions && admin.permissions.length > 0 && (
                            <div className="mt-3 pt-2 border-t border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">Specific Permissions:</p>
                              <div className="flex flex-wrap gap-1">
                                {admin.permissions.map((permission, index) => (
                                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                    {permission.replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <button
                          onClick={() => {
                            setEditingUser(admin);
                            setUserPermissions(admin.permissions || []);
                            setShowPermissionModal(true);
                          }}
                          className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                        >
                          Edit Permissions
                        </button>
                        <button
                          onClick={async () => {
                            const newRole = prompt('Enter new role (super_admin, management_admin, principal, teacher):', admin.role);
                            if (newRole && ['super_admin', 'management_admin', 'principal', 'teacher'].includes(newRole)) {
                              try {
                                const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                                const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/users/${admin.id}`, {
                                  method: 'PUT',
                                  headers: {
                                      'Content-Type': 'application/json',
                                      'admin-email': adminUser.email
                                  },
                                  body: JSON.stringify({ role: newRole })
                                });

                                if (response.ok) {
                                  alert('Role updated successfully!');
                                  loadAdminUsers();
                                } else {
                                  alert('Failed to update role');
                                }
                              } catch (error) {
                                console.error('Error updating role:', error);
                                alert('Failed to update role');
                              }
                            }
                          }}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          Change Role
                        </button>
                        <button
                          onClick={() => openAdminPasswordModal(admin)}
                          className="bg-slate-700 text-white px-3 py-1 rounded text-sm hover:bg-slate-800"
                        >
                          Password
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to ${admin.active ? 'deactivate' : 'activate'} ${admin.name}?`)) {
                              try {
                                const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                                const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/users/${admin.id}`, {
                                  method: 'PUT',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'admin-email': adminUser.email
                                  },
                                  body: JSON.stringify({ active: !admin.active })
                                });

                                if (response.ok) {
                                  alert(`Admin ${admin.active ? 'deactivated' : 'activated'} successfully!`);
                                  loadAdminUsers();
                                } else {
                                  alert(`Failed to ${admin.active ? 'deactivate' : 'activate'} admin`);
                                }
                              } catch (error) {
                                console.error('Error updating admin status:', error);
                                alert(`Failed to ${admin.active ? 'deactivate' : 'activate'} admin`);
                              }
                            }
                          }}
                          className={`${admin.active ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} text-white px-3 py-1 rounded text-sm`}
                        >
                          {admin.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={async () => {
                            const currentAdmin = JSON.parse(localStorage.getItem('adminUser') || '{}');
                            if (admin.id === currentAdmin.id) {
                              alert('You cannot delete your own account!');
                              return;
                            }
                            if (window.confirm(`Are you sure you want to permanently delete ${admin.name}Loading This action cannot be undone.`)) {
                              try {
                                const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                                const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/users/${admin.id}`, {
                                  method: 'DELETE',
                                  headers: {
                                    'admin-email': adminUser.email
                                  }
                                });

                                if (response.ok) {
                                  alert('Admin user deleted successfully!');
                                  loadAdminUsers();
                                } else {
                                  alert('Failed to delete admin user');
                                }
                              } catch (error) {
                                console.error('Error deleting admin:', error);
                                alert('Failed to delete admin user');
                              }
                            }
                          }}
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Password Management Modal */}
      {showAdminPasswordModal && passwordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <form onSubmit={resetAdminUserPassword} className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Password Management</h2>
                  <p className="text-sm text-gray-600 mt-1">{passwordUser.name} · {passwordUser.email}</p>
                </div>
                <button
                  type="button"
                  onClick={closeAdminPasswordModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <input
                    type="checkbox"
                    checked={adminPasswordForm.useDefaultPassword}
                    onChange={(e) => setAdminPasswordForm(current => ({
                      ...current,
                      useDefaultPassword: e.target.checked,
                      forcePasswordChange: e.target.checked && roleSupportsForcedChange(passwordUser.role)
                    }))}
                  />
                  <span className="font-semibold text-gray-800">
                    Use default password ({getDefaultPasswordForRole(passwordUser.role)})
                  </span>
                </label>

                {!adminPasswordForm.useDefaultPassword && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <input
                        type="password"
                        value={adminPasswordForm.newPassword}
                        onChange={(e) => setAdminPasswordForm(current => ({ ...current, newPassword: e.target.value }))}
                        className="w-full border rounded px-3 py-2"
                        minLength={6}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                      <input
                        type="password"
                        value={adminPasswordForm.confirmPassword}
                        onChange={(e) => setAdminPasswordForm(current => ({ ...current, confirmPassword: e.target.value }))}
                        className="w-full border rounded px-3 py-2"
                        minLength={6}
                        required
                      />
                    </div>
                  </div>
                )}

                {adminPasswordForm.useDefaultPassword && roleSupportsForcedChange(passwordUser.role) && (
                  <label className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <input
                      type="checkbox"
                      checked={adminPasswordForm.forcePasswordChange}
                      onChange={(e) => setAdminPasswordForm(current => ({ ...current, forcePasswordChange: e.target.checked }))}
                    />
                    <span className="font-semibold text-amber-900">Require password change on next login</span>
                  </label>
                )}

                {adminPasswordMessage && (
                  <div className={`rounded-md border px-4 py-3 text-sm font-medium ${
                    adminPasswordMessage.type === 'success'
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}>
                    {adminPasswordMessage.text}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeAdminPasswordModal}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900"
                >
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}



      {/* Security User Management Modal */}
      {showSecurityManagement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Security User Management</h2>
                <button
                  onClick={() => setShowSecurityManagement(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Add New Security User Form */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Create New Security User</h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newSecurityForm.email || !newSecurityForm.password || !newSecurityForm.name) {
                    alert('All fields are required');
                    return;
                  }

                  try {
                    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/security-users`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'admin-email': adminUser.email
                      },
                      body: JSON.stringify(newSecurityForm)
                    });

                    if (response.ok) {
                      alert('Security user created successfully!');
                      setNewSecurityForm({ email: '', password: '', name: '' });
                      loadSecurityUsers(); // Refresh list
                    } else {
                      alert('Failed to create security user');
                    }
                  } catch (error) {
                    console.error('Error creating security user:', error);
                    alert('Failed to create security user');
                  }
                }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    placeholder="Email/Username"
                    value={newSecurityForm.email}
                    onChange={(e) => setNewSecurityForm({...newSecurityForm, email: e.target.value})}
                    className="border rounded px-3 py-2"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={newSecurityForm.name}
                    onChange={(e) => setNewSecurityForm({...newSecurityForm, name: e.target.value})}
                    className="border rounded px-3 py-2"
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={newSecurityForm.password}
                    onChange={(e) => setNewSecurityForm({...newSecurityForm, password: e.target.value})}
                    className="border rounded px-3 py-2 md:col-span-2"
                    required
                  />
                  <div className="md:col-span-2">
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
                    >
                      Create Security User
                    </button>
                  </div>
                </form>
              </div>

              {/* Existing Security Users */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-orange-700">Total Users</p>
                    <p className="text-2xl font-bold text-orange-950">{securityUsers.length}</p>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-emerald-700">Active</p>
                    <p className="text-2xl font-bold text-emerald-950">{activeSecurityUsers}</p>
                  </div>
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-rose-700">Inactive</p>
                    <p className="text-2xl font-bold text-rose-950">{securityUsers.length - activeSecurityUsers}</p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <h3 className="text-lg font-semibold text-gray-800">Existing Security Users</h3>
                  <input
                    type="search"
                    value={securitySearch}
                    onChange={(e) => setSecuritySearch(e.target.value)}
                    placeholder="Search security users"
                    className="border border-gray-300 rounded px-3 py-2 md:w-72"
                  />
                </div>
                {securityUsers.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No security users found.</p>
                ) : filteredSecurityUsers.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No security users match your search.</p>
                ) : (
                  filteredSecurityUsers.map((user) => (
                    <div key={user.id} className="border rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-bold text-gray-800">{user.name}</h4>
                            <span className="px-2 py-1 rounded text-xs bg-orange-100 text-orange-800">
                              Security Personnel
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {user.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-gray-600 mb-2">{user.email}</p>

                          {/* Security Personnel Capabilities */}
                          <div className="bg-gray-50 rounded p-3 text-sm">
                            <h5 className="font-medium text-gray-700 mb-2">Capabilities:</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div className="flex items-center space-x-2">
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                <span>Verify parent/guardian security codes</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                <span>Monitor real-time attendance verification</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                <span>Access security dashboard and logs</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                <span>Report system issues to administrators</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          <button
                            onClick={async () => {
                              const newPassword = window.prompt(`Enter a new temporary password for ${user.name}`, 'security123');
                              if (!newPassword) return;

                              try {
                                const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                                const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/security-users/${user.id}/reset-password`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'admin-email': adminUser.email
                                  },
                                  body: JSON.stringify({ newPassword })
                                });
                                const result = await response.json();

                                if (response.ok) {
                                  alert(`Password reset successfully.\nTemporary password: ${result.defaultPassword}`);
                                  loadSecurityUsers();
                                } else {
                                  alert(result.error || 'Failed to reset password');
                                }
                              } catch (error) {
                                console.error('Error resetting security user password:', error);
                                alert('Failed to reset password');
                              }
                            }}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-sm"
                          >
                            Reset Password
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to ${user.active ? 'deactivate' : 'activate'} ${user.name}?`)) {
                                try {
                                  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                                  const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/security-users/${user.id}`, {
                                    method: 'PUT',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'admin-email': adminUser.email
                                    },
                                    body: JSON.stringify({ active: !user.active })
                                  });

                                  if (response.ok) {
                                    alert(`Security user ${user.active ? 'deactivated' : 'activated'} successfully!`);
                                    loadSecurityUsers();
                                  } else {
                                    alert(`Failed to ${user.active ? 'deactivate' : 'activate'} security user`);
                                  }
                                } catch (error) {
                                  console.error('Error updating security user status:', error);
                                  alert(`Failed to ${user.active ? 'deactivate' : 'activate'} security user`);
                                }
                              }
                            }}
                            className={`${user.active ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} text-white px-3 py-1 rounded text-sm`}
                          >
                            {user.active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permission Management Modal */}
      {showPermissionModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Permissions - {editingUser.name}</h2>
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600">
                  Select the management cards that <strong>{editingUser.name}</strong> should have access to:
                </p>
              </div>

              {/* Permission Checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {adminOptions.map((option) => (
                  <div key={option.requiredPermission} className="border rounded-lg p-4">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={userPermissions.includes(option.requiredPermission)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setUserPermissions([...userPermissions, option.requiredPermission]);
                          } else {
                            setUserPermissions(userPermissions.filter(p => p !== option.requiredPermission));
                          }
                        }}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{option.title}</h3>
                        <p className="text-sm text-gray-600">{option.description}</p>
                        <p className="text-xs text-gray-500 mt-1">Permission: {option.requiredPermission}</p>
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
                      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/users/${editingUser.id}`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'admin-email': adminUser.email
                        },
                        body: JSON.stringify({ permissions: userPermissions })
                      });

                      if (response.ok) {
                        alert('Permissions updated successfully!');
                        setShowPermissionModal(false);
                        loadAdminUsers();
                      } else {
                        alert('Failed to update permissions');
                      }
                    } catch (error) {
                      console.error('Error updating permissions:', error);
                      alert('Failed to update permissions');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Permissions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Classes Modal */}
      {showManageClasses && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Manage Classes</h2>
                <button
                  onClick={() => setShowManageClasses(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Add New Class Section */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Class</h3>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="Enter new class name"
                    value={newClass}
                    onChange={(e) => setNewClass(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addClass();
                      }
                    }}
                  />
                  <button
                    onClick={addClass}
                    className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition duration-200"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Existing Classes List */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Existing Classes</h3>
                {classes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No classes found.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {classes.map((className, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-white shadow-sm">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-800">{className}</span>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete "${className}"?`)) {
                                try {
                                  const response = await fetch(
                                    `${API_ENDPOINTS.BASE_URL}/api/classes/${encodeURIComponent(className)}`,
                                    { method: 'DELETE' }
                                  );
                                  if (response.ok) {
                                    alert('Class deleted successfully!');
                                    fetchClasses();
                                  } else {
                                    alert('Failed to delete class');
                                  }
                                } catch (error) {
                                  console.error('Error deleting class:', error);
                                  alert('Failed to delete class');
                                }
                              }
                            }}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition duration-200"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Card Assignment Modal */}
      <AdminCardAssignmentModal
        isOpen={showAdminCardModal}
        onClose={() => setShowAdminCardModal(false)}
      />



    </div>
  );
};

export default AdminDashboard;
